import { ToastProvider } from './Toast'
import type { ReactNode } from 'react'
// Tailwind v4 + brand tokens + Neulis Sans + GIS overrides for the login flow.
import './login.css'

/**
 * Shared shell for the login flow (ported from the web client's login layout).
 *
 * - Desktop: split-screen with a branding `<aside>` and a centered form `<main>`.
 * - Mobile: stacked — branding hero on top, form area below.
 *
 * Wraps everything in {@link ToastProvider} so a toast fired right before a
 * client-side navigation (e.g. email form → OTP step) survives the route change.
 */
export function LoginShell({ children }: { children: ReactNode }) {
  return (
    <ToastProvider>
      <div className="min-h-screen flex flex-col lg:flex-row bg-brand-bg-light text-brand-dark-text font-sans">
        {/* 1. Branding visual panel (Left on Desktop, Top on Mobile) */}
        <aside
          className="relative w-full lg:w-[50%] xl:w-[40%] h-auto text-white flex flex-col items-center overflow-hidden shrink-0"
          aria-label="AMLiQ Portal Branding"
        >
          {/* Background Image with Dark Overlay */}
          <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
            <img
              alt="AMLiQ Visual Branding Backdrop"
              className="absolute max-w-none mix-blend-luminosity object-cover w-full h-full"
              src="/background.png"
            />
            <div className="absolute bg-black/70 inset-0" />
          </div>

          {/* Content Container */}
          <div className="relative z-10 w-full max-w-[448px] h-full flex flex-col justify-between pt-12 lg:pt-[120px] pb-6 lg:pb-12 px-6 lg:px-0">
            {/* Top visual stack */}
            <div className="flex flex-col items-start w-full">
              {/* AMLiQ logo */}
              <div className="w-[180px] lg:w-[284px] h-12 lg:h-[76px] relative shrink-0 mb-6 lg:mb-10">
                <img
                  src="/logo.svg"
                  alt="AMLiQ Logo"
                  className="w-full h-full object-contain filter drop-shadow-[0_2px_8px_rgba(26,153,185,0.2)]"
                />
              </div>

              {/* Cyan separator strip with glow */}
              <div className="bg-brand-cyan h-[3px] lg:h-[4px] w-[32px] lg:w-[48px] rounded-full shadow-[0px_0px_15px_2px_rgba(26,153,185,0.6)] shrink-0 mb-6 lg:mb-10" />

              {/* Brand Slogans */}
              <div className="flex flex-col items-start w-full gap-1 mb-6 lg:mb-[100px]">
                <h2 className="font-sans font-bold leading-[36px] lg:leading-[66px] text-brand-white text-[34px] lg:text-[56px] tracking-[-1.5px] m-0">
                  Simplify.
                </h2>
                <h3 className="font-sans font-bold leading-[36px] lg:leading-[66px] text-brand-cyan text-[34px] lg:text-[56px] tracking-[-1.5px] m-0 whitespace-nowrap">
                  AML Compliance.
                </h3>
              </div>

              {/* Description Subtitle */}
              <p className="font-sans font-normal leading-[24px] lg:leading-[29.25px] text-brand-muted text-[12px] lg:text-[18px] w-full tracking-[0.5px]">
                One portal for client verification, team management and AML compliance resources.
              </p>
            </div>

            {/* Bottom Copyright Section */}
            <div className="border-t border-brand-border pt-6 mt-12 w-full hidden lg:block">
              <p className="font-sans font-normal leading-[20px] text-brand-copyright text-[14px] whitespace-nowrap">
                © 2026 AMLiQ Pty Ltd
              </p>
            </div>
          </div>
        </aside>

        {/* 2. Main Login Area (Right on Desktop, Bottom on Mobile) */}
        <main className="flex-1 flex flex-col justify-center items-center p-6 lg:p-12 xl:p-16 bg-brand-bg-light">
          <div className="w-full max-w-[440px] flex flex-col gap-6">
            {/* Active step form card gets injected here */}
            {children}
          </div>
        </main>
      </div>
    </ToastProvider>
  )
}
